import Phaser from 'phaser';

import { BlackjackMainScene } from '../../scenes/blackjack-main-scene';
// import { DebugContainerVisual } from '../debug/debug-container-visual';
import { AvailableFontSizes, TextService } from '../../services/text-service';
import { ResizeMetrics } from '../../model/resize-metrics';
import { Subscription } from 'rxjs';
import { RoundedNumberViewModel } from './rounded-number-view-model';
import { UxMetrics } from '../../model/ux-metrics';

export class RoundedNumberView extends Phaser.GameObjects.Container {
  private currentResizeMetrics?: ResizeMetrics;
  private subscriptionGroup: Subscription;

  private graphics: Phaser.GameObjects.Graphics;
  private label: Phaser.GameObjects.Text;
  private currentNumber?: number;
  private activeTween?: Phaser.Tweens.Tween; // Store active tween

  constructor(
    scene: BlackjackMainScene,
    x: number,
    y: number,
    private viewModel: RoundedNumberViewModel,
  ) {
    super(scene, x, y);

    // new DebugContainerVisual(scene, this);

    this.subscriptionGroup = new Subscription();

    this.graphics = scene.add.graphics();
    this.add(this.graphics);

    const fontStyle = TextService.fontStyleNormal(
      AvailableFontSizes.Medium,
      this.viewModel.labelFill,
      this.viewModel.defaultFontFamily,
    );

    this.label = this.scene.add.text(0, 0, '', fontStyle).setOrigin(0.5, 0.5);
    this.add(this.label);

    this.setName(RoundedNumberView.name);
    scene.add.existing(this);

    this.subscriptionGroup.add(
      this.viewModel.currentNumber$.subscribe((value) => {
        if (this.currentNumber === undefined) {
          this.currentNumber = value;
          this.setLabel(this.currentNumber);
          return;
        }
        this.animateNumber(this.currentNumber, value, UxMetrics.NumberIncrementDuration);
      }),
    );
  }

  override destroy(fromScene?: boolean | undefined): void {
    this.subscriptionGroup.unsubscribe();

    super.destroy(fromScene);
  }

  public onResize(resizeMetrics: ResizeMetrics) {
    this.currentResizeMetrics = resizeMetrics;
    this.refreshLayout();
  }

  private animateNumber(startNumber: number, endNumber: number, duration: number): void {
    // Stop any existing tween
    if (this.activeTween) {
      this.activeTween.stop();
    }

    const tempObj = { value: startNumber };

    this.activeTween = this.scene.tweens.add({
      targets: tempObj,
      value: endNumber,
      duration: duration,
      ease: Phaser.Math.Easing.Linear,
      onUpdate: () => {
        this.currentNumber = Math.round(tempObj.value); // Round for clarity
        this.setLabel(this.currentNumber);
      },
      onComplete: () => {
        this.activeTween = undefined; // Clear reference when done
      },
    });
  }

  private setLabel(value: number) {
    const formattedValue = value.toLocaleString();
    this.label.text = `${this.viewModel.prefix} ${formattedValue}`;
    this.refreshLayout();
  }

  private refreshLayout(): void {
    if (!this.currentResizeMetrics) {
      return;
    }
    const layoutConfig = this.currentResizeMetrics.layoutConfig;
    const scalingMetrics = this.currentResizeMetrics.scalingMetrics;

    this.label.setFontSize(this.currentResizeMetrics.fontMetrics.Medium);

    const padding = layoutConfig.paddingSm * scalingMetrics.scaleFactor;
    this.setSize(this.label.width + padding * 2, this.label.height + padding);

    this.drawGraphics();
  }

  private drawGraphics(): void {
    this.graphics.clear(); // Clear previous drawings

    this.graphics.fillStyle(this.viewModel.backgroundColor, this.viewModel.backgroundAlpha);
    this.graphics.fillRoundedRect(
      this.width * -0.5,
      this.height * -0.5,
      this.width,
      this.height,
      this.height * 0.1,
    );
    this.graphics.setDepth(1);
  }
}
